/**
 * Contatti sul computer, ridisegnati il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: chi è, e come lo raggiungo?
 * La forma: una rubrica con le lettere a sinistra (iniziali o logo, una riga a
 * contatto, i gesti rapidi chiama/scrivi/WhatsApp al passaggio del mouse) e la
 * scheda accanto a destra, che si aggiorna al clic senza cambiare pagina. In
 * testa la risposta in una frase, coi numeri che prima erano quattro riquadri.
 *
 * Alleggerita (Lorenzo, 26/10): via i quattro numeri, "Ordina" e "Filtra per"
 * (la rubrica è in ordine alfabetico), il riepilogo "anche fornitori / senza
 * recapito" e la tabella a sei colonne. I filtri vecchi nell'indirizzo (aperto,
 * scheda) valgono ancora: se ce n'è uno acceso una riga lo dice. Sul telefono
 * resta ContactsMobile.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { useUrlState } from '../../hooks/useUrlState';
import { Icon, type IconName } from '../../components/ui/Icon';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { EmptyState } from '../../components/ui/EmptyState';
import { useFiltri, type FiltroDef } from '../../components/ui/OrdinaFiltra';
import { leggibile } from '../../components/mobile/MobileKit';
import { euro } from '../../lib/utils';
import { NuovoContattoPopover } from './NuovoContattoPopover';
import {
  FILTRI, TIPO_LABEL, canWriteContatti, contattiQuery, contattoQuery, letteraRubrica, numeroWhatsApp, telefonoLeggibile,
  type FiltroTipo, type RigaContatto,
} from './contactsApi';
import './contacts-desktop.css';

const TIPI_VALIDI = new Set<string>(FILTRI.map((f) => f.key));

/** I filtri che non hanno più un comando a schermo ma valgono ancora dall'indirizzo. */
const NASCOSTI: FiltroDef<RigaContatto>[] = [
  {
    param: 'aperto', label: 'Cosa c’è di aperto', multi: true,
    opzioni: [
      { value: 'scaduto', label: 'con fatture scadute da incassare' },
      { value: 'da-pagare', label: 'che dobbiamo ancora pagare' },
      { value: 'trattative', label: 'con trattative aperte' },
      { value: 'progetti', label: 'con progetti in corso' },
    ],
    test: (r, v) => (v === 'scaduto' ? r.scaduto > 0 : v === 'da-pagare' ? r.daPagare > 0 : v === 'trattative' ? r.trattativeAperte > 0 : v === 'progetti' ? r.progettiAttivi > 0 : false),
  },
  {
    param: 'scheda', label: 'Cosa dice la scheda',
    opzioni: [
      { value: 'senza-recapito', label: 'senza email né telefono' },
      { value: 'anche-fornitori', label: 'sia clienti sia fornitori' },
    ],
    test: (r, v) => (v === 'senza-recapito' ? !r.email && !r.phone : v === 'anche-fornitori' ? r.tipi.includes('CLIENTE') && (r.tipi.includes('FORNITORE') || r.tipi.includes('PROFESSIONISTA')) : false),
  },
];

const mailto = (e: string | null) => (e ? `mailto:${e}` : null);
const tel = (p: string | null) => (p ? `tel:${p.replace(/\s+/g, '')}` : null);
const wa = (p: string | null) => { const n = numeroWhatsApp(p); return n ? `https://wa.me/${n}` : null; };

export function ContactsDesktop() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteContatti(user);
  const [tipoParam, setTipo] = useUrlState<FiltroTipo>('tipo', 'tutti');
  const tipo: FiltroTipo = TIPI_VALIDI.has(tipoParam) ? tipoParam : 'tutti';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [debSearch, setDebSearch] = useState(search);
  const [scelto, setScelto] = useUrlState<string>('c', '', { replace: true });
  const [nuovo, setNuovo] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery(contattiQuery(tipo, debSearch));
  const crea = useMutation({
    mutationFn: (v: { nome: string; tipo: string; email: string | null; phone: string | null }) => api.post<{ key: string }>('/contacts', v),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.ok('Contatto creato', v.nome);
      navigate(`/contatti/${r.key}`);
    },
    onError: (e: any) => toast.alert('Non creato', e?.message ?? 'Riprova'),
  });

  const filtri = useFiltri(NASCOSTI, data?.items);
  const righe = useMemo(() => {
    const all = filtri.applica(data?.items ?? []);
    return [...all].sort((a, b) => leggibile(a.name).localeCompare(leggibile(b.name), 'it', { sensitivity: 'base' }));
  }, [data, filtri]);
  const gruppi = useMemo(() => {
    const m = new Map<string, RigaContatto[]>();
    for (const r of righe) { const l = letteraRubrica(leggibile(r.name)); m.set(l, [...(m.get(l) ?? []), r]); }
    return [...m.entries()];
  }, [righe]);
  const accesi = filtri.attivi > 0
    ? NASCOSTI.flatMap((d) => d.opzioni.filter((o) => filtri.ha(d.param, o.value)).map((o) => o.label))
    : [];

  // La scheda accanto: quella scelta, altrimenti la prima della rubrica.
  const riga = righe.find((r) => r.key === scelto) ?? righe[0] ?? null;

  const c = data?.conta;
  const risposta = !c ? 'Apro la rubrica…'
    : <>In rubrica ci sono <strong>{c.tutti} contatti</strong>: {c.clienti} {c.clienti === 1 ? 'cliente' : 'clienti'}, {c.potenziali} {c.potenziali === 1 ? 'potenziale' : 'potenziali'} e {c.professionisti} {c.professionisti === 1 ? 'professionista' : 'professionisti'}.</>;

  return (
    <div className="page ct2">
      <header className="ct2-hd">
        <div className="ct2-hd-main">
          <SectionMark icon="users" className="ct2-sez" />
          <div className="min-w-0">
            <h1 className="ct2-title">Contatti</h1>
            <p className="ct2-risposta">{risposta}</p>
          </div>
        </div>
        {canWrite && (
          <div className="relative ct2-nuovo">
            <button className="btn btn-primary" onClick={() => setNuovo(true)}><Icon name="plus" /> Nuovo contatto</button>
            {nuovo && (
              <NuovoContattoPopover pending={crea.isPending} onClose={() => setNuovo(false)} onConfirm={(v) => { setNuovo(false); crea.mutate(v); }} />
            )}
          </div>
        )}
      </header>

      <div className="ct2-bar">
        <div className="ct2-seg" role="tablist" aria-label="Chi">
          {FILTRI.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={tipo === f.key} className="ct2-seg-btn" onClick={() => setTipo(f.key)}>
              {f.label}{c && <span className="n">{c[f.conta]}</span>}
            </button>
          ))}
        </div>
        <label className="ct2-search">
          <Icon name="search" size={13} />
          <input aria-label="Cerca un contatto" placeholder="Nome, email, telefono o partita IVA" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>

      {accesi.length > 0 && (
        <div className="ct2-filtro">
          <Icon name="filter" size={12} /> Stai vedendo solo i contatti {accesi.join(', ')}.
          <button type="button" onClick={filtri.clear}>Mostra tutti</button>
        </div>
      )}

      <div className="ct2-corpo">
        <section className="ct2-rubrica" aria-label="Rubrica">
          {isLoading && !data ? (
            <div className="skeleton-col p-3"><span className="skeleton sk-block" /><span className="skeleton sk-block" /><span className="skeleton sk-block" /></div>
          ) : righe.length === 0 ? (
            <EmptyState
              brand
              title="Nessun contatto"
              description={search ? 'Nessuno corrisponde alla ricerca.' : accesi.length ? 'Nessuno corrisponde ai filtri.' : 'La rubrica è vuota.'}
              action={search ? <button className="btn" onClick={() => setSearch('')}>Svuota la ricerca</button> : undefined}
            />
          ) : (
            gruppi.map(([l, rs]) => (
              <div key={l} className="ct2-lettera">
                <div className="ct2-lettera-t">{l}</div>
                {rs.map((r) => (
                  <RigaRubrica
                    key={r.key}
                    r={r}
                    scelta={riga?.key === r.key}
                    onScegli={() => setScelto(r.key)}
                    onApri={() => navigate(`/contatti/${r.key}`)}
                  />
                ))}
              </div>
            ))
          )}
        </section>

        <aside className="ct2-accanto" aria-label="Scheda del contatto">
          {riga ? <SchedaAccanto r={riga} onApri={() => navigate(`/contatti/${riga.key}`)} /> : (
            <div className="ct2-accanto-vuota">Scegli un contatto dalla rubrica.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

/** Una riga della rubrica: chi è, cos'è, e se c'è qualcosa in sospeso. I gesti compaiono al passaggio. */
function RigaRubrica({ r, scelta, onScegli, onApri }: { r: RigaContatto; scelta: boolean; onScegli: () => void; onApri: () => void }) {
  const nome = leggibile(r.name);
  const cosa = [r.tipi.map((t) => TIPO_LABEL[t]).join(' e '), r.city].filter(Boolean).join(' · ');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (scelta) ref.current?.scrollIntoView({ block: 'nearest' }); }, [scelta]);
  return (
    <div
      ref={ref}
      className={`ct2-riga${scelta ? ' is-scelta' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={scelta}
      onClick={onScegli}
      onDoubleClick={onApri}
      onKeyDown={(e) => { if (e.key === 'Enter') onApri(); else if (e.key === ' ') { e.preventDefault(); onScegli(); } }}
    >
      <BrandLogo name={nome} size={32} />
      <span className="ct2-riga-tx">
        <span className="t">{nome}</span>
        <span className="s">{cosa}</span>
      </span>
      {r.scaduto > 0 && <span className="ct2-riga-flag" title={`Ci deve ${euro(r.scaduto)} scaduti`}>{euro(r.scaduto)}</span>}
      <span className="ct2-gesti" onClick={(e) => e.stopPropagation()}>
        <Gesto icon="phone" label={`Chiama ${nome}`} href={tel(r.phone)} />
        <Gesto icon="mail" label={`Scrivi a ${nome}`} href={mailto(r.email)} />
        <Gesto icon="message" label={`WhatsApp a ${nome}`} href={wa(r.phone)} esterno />
      </span>
    </div>
  );
}

function Gesto({ icon, label, href, esterno }: { icon: IconName; label: string; href: string | null; esterno?: boolean }) {
  if (!href) return <span className="ct2-gesto is-off" aria-hidden="true"><Icon name={icon} size={14} /></span>;
  return (
    <a className="ct2-gesto" href={href} title={label} aria-label={label} {...(esterno ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <Icon name={icon} size={14} />
    </a>
  );
}

/** La scheda accanto: chi è, i gesti grandi, i recapiti e in una frase quanto conta per noi. */
function SchedaAccanto({ r, onApri }: { r: RigaContatto; onApri: () => void }) {
  const navigate = useNavigate();
  // La chiave è "client:<id>", "supplier:<id>" o "deal:<id>" (la stessa dell'indirizzo /contatti/:kind/:id).
  const [kind, id] = r.key.split(/[:/]/) as [string, string];
  const { data: d } = useQuery({ ...contattoQuery(kind, id), staleTime: 60_000 });
  const nome = leggibile(r.name);
  const email = d?.recapiti.email ?? r.email;
  const phone = d?.recapiti.phone ?? r.phone;
  const sede = d ? [d.anagrafica.addressLine, d.anagrafica.city].filter(Boolean).join(', ') : r.city;
  const referenti = (d?.referenti ?? []).slice(0, 3);
  const aperte = (d?.trattative ?? []).filter((t) => t.outcome === 'APERTO').slice(0, 3);

  // Quanto conta per noi, in una frase coi numeri dentro.
  const pezzi: string[] = [];
  if (r.tipi.includes('CLIENTE')) pezzi.push(r.ricavoYtd > 0 ? `Quest’anno ha fatturato ${euro(r.ricavoYtd)}` : 'Quest’anno non ha ancora fatture');
  if (r.canoneMensile > 0) pezzi.push(`paga ${euro(r.canoneMensile)} al mese di canone`);
  if (r.scaduto > 0) pezzi.push(`ci deve ${euro(r.scaduto)} già scaduti`);
  if (r.tipi.includes('PROFESSIONISTA') || r.tipi.includes('FORNITORE')) {
    if (r.speso > 0) pezzi.push(`gli abbiamo pagato ${euro(r.speso)}`);
    if (r.daPagare > 0) pezzi.push(`dobbiamo ancora pagargli ${euro(r.daPagare)}`);
  }
  if (r.trattativeAperte > 0) pezzi.push(`${r.trattativeAperte === 1 ? 'ha una trattativa aperta' : `ha ${r.trattativeAperte} trattative aperte`} per ${euro(r.potenziale)}`);
  const frase = pezzi.length ? `${pezzi.join(', ').replace(/^./, (x) => x.toUpperCase())}.` : null;

  return (
    <div className="ct2-scheda">
      <div className="ct2-scheda-hd">
        <BrandLogo name={nome} size={56} />
        <div className="min-w-0">
          <h2 className="ct2-scheda-t">{nome}</h2>
          <div className="ct2-scheda-s">{[r.tipi.map((t) => TIPO_LABEL[t]).join(' e '), r.sector].filter(Boolean).join(' · ')}</div>
        </div>
      </div>

      <div className="ct2-azioni">
        <GestoGrande icon="phone" label="Chiama" href={tel(phone)} />
        <GestoGrande icon="mail" label="Scrivi" href={mailto(email)} />
        <GestoGrande icon="message" label="WhatsApp" href={wa(phone)} esterno />
        <button type="button" className="ct2-azione is-apri" onClick={onApri}><span className="ico"><Icon name="arrowRight" size={16} /></span><span>Apri la scheda</span></button>
      </div>

      {frase && <p className="ct2-frase">{frase}</p>}

      <dl className="ct2-dati">
        <div><dt>Email</dt><dd>{email ? <a href={`mailto:${email}`}>{email}</a> : <span className="ct2-manca">non ce l’abbiamo</span>}</dd></div>
        <div><dt>Telefono</dt><dd>{phone ? <a href={tel(phone)!}>{telefonoLeggibile(phone)}</a> : <span className="ct2-manca">non ce l’abbiamo</span>}</dd></div>
        {sede && <div><dt>Dove</dt><dd>{sede}</dd></div>}
        {d?.anagrafica.vat && <div><dt>Partita IVA</dt><dd className="mono">{d.anagrafica.vat}</dd></div>}
      </dl>

      {referenti.length > 0 && (
        <div className="ct2-blocco">
          <div className="ct2-blocco-t">Persone da sentire</div>
          {referenti.map((p) => (
            <div key={p.id} className="ct2-persona">
              <BrandLogo name={p.fullName} size={28} />
              <span className="tx"><span className="t">{p.fullName}</span><span className="s">{[p.role, p.phone ? telefonoLeggibile(p.phone) : p.email].filter(Boolean).join(' · ')}</span></span>
            </div>
          ))}
        </div>
      )}

      {aperte.length > 0 && (
        <div className="ct2-blocco">
          <div className="ct2-blocco-t">Trattative aperte</div>
          {aperte.map((t) => (
            <button key={t.id} type="button" className="ct2-tratt" onClick={() => navigate(`/trattative/${t.id}`)}>
              <span className="t">{t.title}</span>
              <span className="s">{t.stage}</span>
              <span className="v">{t.amount > 0 ? euro(t.amount) : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GestoGrande({ icon, label, href, esterno }: { icon: IconName; label: string; href: string | null; esterno?: boolean }) {
  const corpo = <><span className="ico"><Icon name={icon} size={16} /></span><span>{label}</span></>;
  if (!href) return <span className="ct2-azione is-off" title={`${label}: manca il recapito`}>{corpo}</span>;
  return <a className="ct2-azione" href={href} {...(esterno ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{corpo}</a>;
}
